import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useParams } from 'react-router';
import ReactSwipe from 'react-swipe';
import axios from 'axios';
import { useCurrentUser } from "../app";
import { EmptyView, LoadingView } from "./status";
import { getByIds } from "../get_by_ids";
import { ThumbnailFileView, objToEl } from "./misc";
import { LocalStorageCache, isFunction, isString, last} from "../utils";
import { getRandomColor, useRerender } from '../utils/common';
import { playSound } from '../utils/audio';
import { DateView } from './date';
import { FileUploader, validateAndUploadFiles } from '../file_uploader';
import { Popup } from './popups';
import { chatWindowsContainer, chatWindowKeys, maximizeChatWindow, useChatWindowMinimized } from './chat_windows';
import ProfileAvatar from "../images/avatar.png"
import { EMOJIS, MILLIS_IN_HOUR } from '../constants';
import { GenericException } from './errors';
import { broadcaster, useBroadcastedState } from '../utils/events';
import { URL_REGEX, getUKey, useOnScroll } from './utils';
import { EVENT_ALLSPARK_MESSAGE_RECEIVED, isAllsparkConnected } from '../allsparkrt';
import CHAT_DEFAULT_BG_IMAGE from '../images/chat_bg.jpg'
import NEW_PING_SOUND from "../audio/ping.mp3"
import "../css/chat.css"
import Clipboard from '../utils/clipboard';
import {binarySearch, sortedList} from '../utils/array';
import { Settings, X as XMarkIcon, Bell, BellOff, Plus, Smile, MapPinned, CircleArrowRight, Minimize2, EllipsisVertical, SquarePen, ChevronDown, LoaderCircle, Forward, CheckCheck, Check, CircleAlert, Contact, WandSparkles } from 'lucide-react';
import EventTracker, { PAGE_EVENTS } from "../eventsTracker";

const SESSION_USER_ADMIN = 10;
const SESSION_USER_MODERATOR = 15;
const SESSION_USER_GENERAL = 20;
const SESSION_USER_BLOCKED = 29;
const SESSION_USER_PENDING = 30
const SESSION_USER_BANNED = 40
const SESSION_USER_UNAUTHORIZED = 50

const CHAT_TEXT_CLASS = "tw-text-sm tw-leading-snug tw-text-gray-900 tw-px-2 tw-py-0.5 w3-can-wrap w3-word-wrap";
const CHAT_CHIP_CLASS = "tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-solid tw-border-gray-300 tw-bg-white tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-sky-700 tw-shadow-sm tw-cursor-default";
const CHAT_CHIP_ROW_CLASS = "tw-flex tw-flex-wrap tw-gap-1 tw-px-2 tw-pt-1 tw-pb-1.5";
const CHAT_MENU_TRIGGER_CLASS = "w3-display-topright tw-px-1 tw-pt-0.5 tw-text-gray-400 hover:tw-text-gray-600 tw-cursor-pointer";


const INBOX_MESSAGE_TYPE_SIMPLE_MESSAGE = 1003;
const INBOX_MESSAGE_TYPE_SESSION_UPDATE = 1009;
const INBOX_MESSAGE_TYPE_SESSION_USER_UPDATE = 1010;
const INBOX_MESSAGE_TYPE_APP = 1015;
const INBOX_MESSAGE_TYPE_WA_MSG = 1018;
const INBOX_MESSAGE_TYPE_WA_REPLY_MSG = 1019;  // deprecated remove gradually
const INBOX_MESSAGE_TYPE_WA_INCOMING_MSG = 1020;
const INBOX_MESSAGE_TYPE_WA_TEMPLATE_MSG = 1021;
const INBOX_MESSAGE_TYPE_WA_INFO = 1022;
const INBOX_MESSAGE_TYPE_DEBUG_LOG = 8001;
const INBOX_MESSAGE_TYPE_PAGES_VIEWED = 6000;

const NOTIFICATION_FLAG_ONLY_WHEN_TAGGED = 20;
const NOTIFICATION_FLAG_ALL = 30;

const TYPE_ERROR = -1;
const TYPE_INBOX_ENTRY_ADDED = 13

var url_previews_cache = new LocalStorageCache("url_previews", 50);

  

broadcaster.add_event_listener(
    EVENT_ALLSPARK_MESSAGE_RECEIVED,
    (msg) => {
        /* broadcast so chat windows can listen to cs_{inbox_id} channel */
        if(broadcaster.broadcast_event("im:" + msg.dest_session_id, msg) === 0){
            if(broadcaster.broadcast_event("chat_not_handled", msg) === 0){
                playSound(NEW_PING_SOUND);
                if(isString(msg.payload)){
                    const user_id = msg.src_id;
                    getByIds({"user_ids": [user_id]}).then(
                        (data) => {
                            Popup.toast(
                                <div onClick={() => ChatSession.open(msg.dest_session_id)} >
                                  {`${data.users[user_id]?.name || "🗨"}: ${msg.payload}`}
                                </div>,
                                6000
                            )
                        }
                    );
                }
            }
        }
    }
);

function DateViewWithInfo({im, session_data, cur_user_message}){
    const is_user_in_session = session_data.is_user_in_session;
    return (
        <div className='w3-flex-inline tw-items-center tw-gap-1 tw-px-1 tw-text-gray-500'
            onClick={
                cur_user_message
                || (
                    is_user_in_session?.session_user_type <= SESSION_USER_MODERATOR
                )
                    ?   (evt) => {
                            evt.stopPropagation();
                            Popup.showContextMenu(
                                evt.target,
                                <InboxMessageInfo inbox_message={im} />
                            )
                        }
                    :   null
            }
        >
            {
                im.info
                ?   <div className="tw-flex tw-items-center">
                        {
                            im.info.wa_r 
                            ?   <CheckCheck className='tw-text-sky-500' size={14} />
                            :   im.info.wa_d
                                ?   <CheckCheck size={14} />
                                :   im.info.wa_s || im.info.s1
                                    ?   <Check size={14} />
                                    :   im.info.wa_ns || im.info.f
                                        ?   <XMarkIcon size={14} />
                                        :   null
                        }
                    </div>
                :   null
            }
            <DateView millis={im.created_at} className="tw-text-[11px] tw-leading-4 tw-text-gray-500"/>
            {im.info?.wa_errors ? <CircleAlert className='w3-text-red' size={14} /> : null}
        </div>
    );
}

function InboxMessageInfo({inbox_message:im}){
    const [info, setInfo] = useState(null);
    const [errors, setErrors] = useState(null);
    const [wa_errors, setWAErrors] = useBroadcastedState("wa_errors")

    useEffect(
        () => {
            axios.get(
                "/session/im_info",
                {"params": {"session_id": im.inbox_id, "created_at": im.created_at}}
            ).then(
                (resp) => {
                    resp.data.errors && setErrors(resp.data.errors);
                    resp.data.info && setInfo(resp.data.info);
                    if (resp.data.info?.wa_errors && !wa_errors) {
                        fetch('https://storage.googleapis.com/sukhiba/mobilefresh/meta%20errors-1.json')
                            .then((res) => res.json())
                            .then((data) => {
                                setWAErrors(data)
                            })
                    }
                }
            )
        }, []
    );
    if(errors){
        return <GenericException ex={errors} />
    }
    if(!info){
        return <LoadingView title="" height="100px" />
    }
    if( Object.keys(info).length === 0){
        return <div className='w3-padding'>Sorry No information available</div>
    }
    return (
        <table className='w3-table'>
            <tbody>
                {
                    info.s0
                    ?   <tr><td>Templated</td><td>{info.s0}</td></tr>
                    :   null
                }
                {
                    info.s1
                    ?   <tr><td>Server Sent</td><td>{info.s1}</td></tr>
                    :   null
                }
                {
                    info.wa_f
                    ?   <tr><td>Facebook failed</td><td>{info.wa_f}</td></tr>
                    :   null
                }
                {
                    info.wa_ns
                    ?   <tr><td>Not Sent/No credits</td><td>{info.wa_ns}</td></tr>
                    :   null
                }
                {
                    info.wa_s
                    ?   <tr><td>Facebook Sent</td><td>{info.wa_s}</td></tr>
                    :   null
                }
                {
                    info.wa_d
                    ?   <tr><td>Facebook Delivered</td><td>{info.wa_d}</td></tr>
                    :   null
                }
                {
                    info.wa_r
                    ?   <tr><td>Facebook Read</td><td>{info.wa_r}</td></tr>
                    :   null
                }
                {
                    info.wa_click && Object.entries(info.wa_click).map(
                        ([k, v]) => <tr><td>Click: {k}</td><td>{v}</td></tr>
                    )
                }
                {
                    info.wa_errors && wa_errors && Object.entries(info.wa_errors).map(
                        ([k, v]) => <tr key={k} className='tw-text-red-500'><td>Error: {wa_errors.error_codes.find(e => e.error_code == k)?.description || k}</td><td>{v}</td></tr>
                    )
                }
                {
                    info.exception
                    ?  <tr><td>Exception</td><td className="tw-max-w-xs">{objToEl(info.exception)}</td></tr>
                    :   null
                }
            </tbody>             
        </table>
    );
}

function SimpleMessageContextMenu({im, session_data}){
    const copyMessage = () => {
        /* TODO: per message processing here */
        Clipboard.copy(
            im.data?.txt || im.data?.contacts?.map((contact) => contact.phones?.map((phone) => phone.phone).join(", ")).join(", ")
        );
        Popup.toast("Copied");
    }

    return (
        <div className="w3-list w3-list-bordered">
            <div className='w3-padding-8' onClick={copyMessage}>Copy</div>
            {SimpleMessageContextMenu.Options && <SimpleMessageContextMenu.Options im={im} session_data={session_data} />}
        </div>
    );
}

function AiFollowUpBadge() {
    return (
        <span className="tw-inline-flex tw-items-center tw-gap-0.5 tw-rounded-full tw-border tw-border-violet-200 tw-bg-violet-50 tw-px-1.5 tw-py-px tw-text-[10px] tw-leading-4 tw-font-medium tw-text-violet-700">
            <WandSparkles size={11} aria-hidden="true" /> AI follow-up
        </span>
    );
}

function SimpleMessage({session_data, user, im, txt, action_txt, files, tags, created_at, header, contacts, location}){
    const cur_user_message = !im.src_id || im.src_id == user?._id;
    const is_user_in_session = session_data?.is_user_in_session;
    const is_ai_follow_up = im.data1?.ai_follow_up;
    txt = txt || im.payload
         
    if(txt && (!im.is_scanned_for_links || im.txt !== txt)){
        /* one time check: super important */
        im.is_scanned_for_links = true;
        im.txt = txt.replace?.(
            URL_REGEX,
            (url) => `<a href="${url}" target="_blank">${url}</a>`
        );
    }
    const header_context = im.data?.context;
    return (
        <div className='w3-row tw-mx-4 tw-my-1' id={im.created_at}>
            <div className={`${cur_user_message ? "w3-right": "w3-left"} w3-bounded w3-relative`}>
                {header}
                <div className={`${cur_user_message ? "app-chat-speech-bubble-right": "app-chat-speech-bubble-left"}`}>
                    <span className="app-chat-speech-bubble-tail-container"></span>    
                    <div className={CHAT_MENU_TRIGGER_CLASS}
                        onClick={
                            (evt) => {
                                EventTracker.track(PAGE_EVENTS.CLICKED_ON_ARROW_IN_CHAT);
                                Popup.showContextMenu(
                                evt.target, <SimpleMessageContextMenu im={im} session_data={session_data}/>
                            )}
                        }
                    >
                        <ChevronDown size={16} />
                    </div>
                    {is_ai_follow_up && (
                        <div className="tw-flex tw-px-2 tw-pr-8 tw-pt-1">
                            <AiFollowUpBadge />
                        </div>
                    )}
                    {
                        /* forwarded / frequently forwarded */
                        (header_context && (header_context.forwarded || header_context.frequently_forwarded))
                        ?  <div className='tw-flex tw-gap-1 tw-items-center tw-px-2 tw-pr-6 tw-pt-1 tw-text-xs tw-italic tw-text-gray-500'>
                                <Forward size={14} />
                                <span>
                                    {im.data.context.frequently_forwarded ? 'Forwarded many times' : 'Forwarded'}
                                </span>
                            </div>
                        : null
                    }
                    {
                        /* if this message is sent from business app (smb_echo) */
                        im.data?.fb?._origin === "smb_echo"
                            ? <div className='tw-px-2 tw-flex tw-py-0.5 tw-items-center'>
                                <span className='tw-text-xxs tw-text-gray-400'>Business App</span>
                            </div>
                            : is_ai_follow_up ? null : <div className='tw-pt-1'>
                                {/* small top empty line to not merge icons with text */}
                                </div>
                    }
                    {
                        im.user
                        ?   <div className="tw-text-xs tw-font-semibold tw-px-2 tw-pr-6"
                                style={{color: getRandomColor(im.user.name)}}
                            >
                                {im.user.name}
                            </div>
                        :  null
                    }
                    {
                        header_context?.id && im.data1?.ref ? (() => {
                            const ref_id = im.data1.ref[1];
                            const ref_im = session_data.already_added_messages[ref_id]
                            const extracted_message = extractMessage(ref_im).toString();
                            const extracted_file = ref_im?.data?.fb?.interactive?.header?.image?.link                            
                                                   || ref_im?.payload1 && JSON.parse(ref_im.payload1)[0]
                                                   || ref_im?.data?.media_urls?.[0]?.url
                            const display_message = extracted_message.length > 40 ? extracted_message.substring(0, 40) + '...' : extracted_message;
                        
                            return (
                                <div 
                                    className="tw-mx-1 tw-mt-1 tw-mb-0.5 tw-px-2 tw-py-1 tw-text-xs tw-text-gray-600 tw-bg-black/5 tw-rounded-md tw-border-0 tw-border-l-4 tw-border-solid tw-border-green-500 tw-cursor-pointer w3-flex w3-align-middle"
                                    onClick={() => {
                                        const ref_div = document.getElementById(ref_id);
                                        if(!ref_div) return;
                                        ref_div.scrollIntoView({ behavior: 'smooth' });
                                        ref_div.classList.add("w3-animate-opacity", "w3-light-gray");
                                        ref_div.addEventListener('animationend', () => {
                                            ref_div.classList.remove("w3-animate-opacity", "w3-light-gray");
                                        }, { once: true });
                                    }}                              
                                >
                                    {extracted_file && (
                                        <div className="w3-col s1 w3-center">
                                        <ThumbnailFileView file={extracted_file} onClick={() => {}}/>
                                        </div>
                                    )}
                                    <div className={`w3-col ${extracted_file ? 's11 w3-padding-left' : 's12'}`}>
                                        {display_message}
                                    </div>
                                </div>
                            );
                        })() : null
                    }
                    {
                        files?.map(
                            (file, i) => file ? <ThumbnailFileView file={file} key={i} /> : null
                        )
                    }
                    {
                        contacts?.map((contact, i) => (
                            <div className="tw-border-b tw-p-2" key={i} style={{ minWidth: "250px" }}>
                                <div className="tw-flex tw-items-center tw-gap-x-2">
                                        <Contact  size={28} />
                                    <div className="">
                                    {
                                        contact && contact.name 
                                        ? (
                                            <p className="w3-small w3-text-black">
                                                {contact.name.formatted_name}
                                            </p>
                                        )
                                        : null 
                                    }
                                    {
                                        (contact && contact.phones && contact.phones.length > 0) 
                                        ?  (
                                            <p className="w3-tiny w3-text-gray">
                                                {contact.phones?.map((phone) => phone.phone).join(", ")}
                                            </p>
                                        )
                                        : null
                                    }
                                    {
                                        (contact && contact.emails && contact.emails.length > 0) 
                                        ? (
                                            <p className="w3-tiny w3-text-gray">
                                                {contact.emails?.map((email) => email.email).join(", ")}
                                            {contact.emails.map((email) => email.email).join(", ")}
                                            </p>
                                        )
                                        : null
                                    }
                                    </div>
                                </div>
                            </div>
                        ))
                    }
                    {
                        location 
                        ? <div className='tw-text-sm tw-px-2 tw-py-1'>
                            <span className='tw-mr-2 tw-inline'>{location.address || "Location"}</span>
                            <MapPinned className='tw-text-sky-600 tw-inline tw-align-text-top tw-cursor-pointer' size={16}
                                onClick={() => {
                                    window.open(`https://maps.google.com/?q=${location.latitude},${location.longitude}`, '_blank')
                                }} />
                        </div> : null
                    }
                    {
                        im.txt 
                        ?   <div className={`${CHAT_TEXT_CLASS} tw-pr-6 tw-pb-1`}
                                dangerouslySetInnerHTML={{__html: im.txt}} 
                            />
                        :   action_txt
                            ?   <div className="tw-text-xs tw-italic tw-text-gray-500 tw-px-2 tw-pr-6 tw-py-1 w3-can-wrap w3-word-wrap"
                                    dangerouslySetInnerHTML={{__html: action_txt}} 
                                />
                            :   null
                    }
                    {
                        im.data?.reactions
                        ?  <div className='w3-flex-inline w3-white w3-border w3-round-xxlarge'>
                            {
                                Object.entries(im.data.reactions).map(
                                    ([_user_id, reaction]) =>  <div key={_user_id}>{reaction.emoji}</div>
                                )
                            }
                        </div>
                        : null
                    }
                    {
                        tags?.length
                        ?   <div className={CHAT_CHIP_ROW_CLASS}>
                                {
                                    tags.map(
                                        (tag, i) => <div key={i} className={CHAT_CHIP_CLASS}>{tag}</div>
                                    )
                                }
                            </div>
                        :   null
                    }
                </div>
                <DateViewWithInfo im={im} cur_user_message={cur_user_message} session_data={session_data} />                
            </div>
        </div>
    );
}

function InteractiveMessage({session_data, is_user_in_session, cur_user_message, user, im}){
    if (
        im.data.fb.interactive?.type == "button" || im.data.fb.interactive?.type == "list"
        || im.data.fb.interactive?.type == "cta_url" || im.data.fb.interactive?.type == "location_request_message"
        || im.data.fb.interactive?.type == "product_list" || im.data.fb.interactive?.type == "order_details"
        || im.data.fb.interactive?.type == "carousel" ||  im.data.fb.interactive?.type == "flow"
        || im.data.fb.interactive?.type == "request_contact_info"
    ) {
        return (
            <div className='w3-row tw-mx-4 tw-my-1' id={im.created_at}>
                <div className={`${cur_user_message ? "w3-right": "w3-left"} w3-bounded`}>
                    <div className={`${cur_user_message ? "app-chat-speech-bubble-right": "app-chat-speech-bubble-left"}`}>
                        <div className={CHAT_MENU_TRIGGER_CLASS}
                            onClick={
                                (evt) => {
                                    EventTracker.track(PAGE_EVENTS.CLICKED_ON_ARROW_IN_CHAT);
                                    Popup.showContextMenu(
                                    evt.target, <SimpleMessageContextMenu im={im} session_data={session_data}/>
                                )}
                            }
                        >
                            <ChevronDown size={16} />
                        </div>

                        <span className="app-chat-speech-bubble-tail-container"></span>
                        {im.data1?.ai_follow_up && (
                            <div className="tw-flex tw-px-2 tw-pr-8 tw-pt-1">
                                <AiFollowUpBadge />
                            </div>
                        )}
                        {
                            im.user
                            ?   <div className="tw-text-xs tw-font-semibold tw-px-2 tw-pr-6 tw-pt-1"
                                    style={{color: getRandomColor(im.user.name)}}
                                >
                                    {im.user.name}
                                </div>
                            :   null
                        }
                        {
                            im.data.fb.interactive.header?.image?.link ||
                                im.data.fb.interactive.header?.video?.link ||
                                im.data.fb.interactive.header?.document?.link
                                ? <ThumbnailFileView file={im.data.fb.interactive.header.image?.link
                                    || im.data.fb.interactive.header.video?.link
                                    || im.data.fb.interactive.header.document?.link}
                                    className="w3-image tw-aspect-square tw-max-w-24" />
                                : null
                        }
                        <div className={`${CHAT_TEXT_CLASS} tw-pr-6 tw-pt-1 tw-whitespace-pre-wrap`}>{im.data.fb.interactive.body?.text}</div>
                        {
                            im.data.fb.interactive.type === "carousel"
                                && im.data.fb.interactive.action?.cards
                                && im.data.fb.interactive.action.cards.length
                                ? <div className="chat-carousel-container tw-flex tw-overflow-x-auto tw-gap-2 tw-p-1 tw-w-full">
                                    {im.data.fb.interactive.action.cards.map((card, idx) => (
                                        <div key={idx} className="tw-min-w-[220px] tw-max-w-[220px] tw-w-[220px] tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-300 tw-flex tw-flex-col tw-overflow-hidden tw-flex-shrink-0 tw-mb-1">
                                            {card.header?.image?.link && (
                                                <ThumbnailFileView
                                                    file={card.header.image.link}
                                                    className="tw-w-full tw-h-[140px] tw-object-cover tw-bg-gray-100 tw-border-b tw-border-gray-200"
                                                />
                                            )}
                                            <div className="tw-px-2.5 tw-py-2 tw-flex-grow tw-text-[13px] tw-text-gray-800 tw-whitespace-pre-wrap tw-leading-snug">
                                                {card.body?.text}
                                            </div>
                                            {card.action?.parameters?.url && (
                                                <div className="tw-p-2 tw-border-t tw-border-gray-100 tw-text-center tw-bg-gray-50">
                                                    <a href={card.action.parameters.url} target="_blank" rel="noopener noreferrer"
                                                        className={`${CHAT_CHIP_CLASS} tw-cursor-pointer hover:tw-bg-gray-50`} style={{ textDecoration: "none" }}>
                                                        {card.action.parameters.display_text || "View"}
                                                    </a>
                                                </div>
                                            )}
                                            {card.action?.buttons?.map((btn, i) => (
                                                btn.type === 'quick_reply' ? (
                                                    <div key={i} className="tw-p-2 tw-border-t tw-border-gray-100 tw-text-center tw-bg-gray-50">
                                                        <div className={CHAT_CHIP_CLASS}
                                                            title={btn.quick_reply.id}
                                                        >
                                                            {btn.quick_reply.title}
                                                        </div>
                                                    </div>
                                                ) : null
                                            ))}
                                        </div>
                                    ))}
                                </div>
                                : null
                        }
                        <div className={CHAT_CHIP_ROW_CLASS}>
                        {
                            im.data.fb.interactive.action?.buttons?.map(
                                (button_data) => {
                                    if(button_data.type == "reply"){
                                        let {id, title} = button_data.reply;
                                        if (typeof title !== 'string') {
                                            return
                                        }
                                        return (
                                            <div key={id} title={id} className={CHAT_CHIP_CLASS}>{title}</div>
                                        )
                                    }
                                    return null;
                                }
                            )
                        }
                        {
                            im.data.fb.interactive.action?.name == "cta_url"
                            ?    <div className={CHAT_CHIP_CLASS}>
                                    <a href={im.data.fb.interactive.action.parameters.url} target="_blank" rel="noopener noreferrer" className="tw-text-inherit" style={{ textDecoration: "none" }}>
                                        {im.data.fb.interactive.action.parameters.display_text}
                                    </a>
                                </div>
                            :   null
                        }
                        {
                            im.data.fb.interactive.action?.name == "send_location"
                                ? <div className={CHAT_CHIP_CLASS}>
                                    Send Location
                                </div>
                                : null
                        }
                        {
                            im.data.fb.interactive.action?.name == "request_contact_info"
                                ? <div className="tw-text-xs tw-italic tw-text-gray-500">
                                    Contact Info Requested
                                </div>
                                : null
                        }
                        {
                            im.data.fb.interactive.action?.sections?.map(
                                (sections_data) => {
                                    return <div key={getUKey(sections_data)} className="tw-w-full tw-flex tw-flex-wrap tw-gap-1">
                                        <p className="tw-w-full tw-text-xs tw-font-semibold tw-text-gray-600 tw-m-0">{sections_data.title}:</p>
                                        {sections_data.rows?.map((rows) => {
                                            let { id, title } = rows;
                                            return (
                                                <div key={id || ""} title={id} 
                                                    className={CHAT_CHIP_CLASS}
                                                >{title}</div>
                                            )
                                        })}
                                        {
                                            sections_data.product_items
                                            ? <div className="tw-text-xs tw-text-sky-700 tw-cursor-pointer" onClick={() =>  Popup.show("Payload", <pre>{JSON.stringify(sections_data.product_items, null, 4)}</pre>)}>View</div>
                                            : null
                                        }
                                    </div>
                                }
                            )
                        }
                        {
                            im.data.fb.interactive.action?.name === "review_and_pay"
                                ? <div className="tw-w-full tw-text-xs tw-text-gray-800">
                                    <div>
                                        <span className="w3-bold">Status:</span> {im.data.fb.interactive.action.parameters.order.status || "—"}
                                    </div>
                                    {
                                        im.data.fb.interactive.action.parameters.order.items && im.data.fb.interactive.action.parameters.order.items.length > 0
                                            ? (
                                                <div className="w3-margin-bottom">
                                                    <div className="w3-bold w3-margin-bottom-4">Items:</div>
                                                    <ul className="w3-ul">
                                                        {
                                                            im.data.fb.interactive.action.parameters.order.items.map((item, i) => (
                                                                <li key={i} className="tw-p-1">
                                                                    <span>{item.quantity ? item.quantity + ' x ' : ""} {item.name || "Unnamed item"}</span>
                                                                    {item.amount && (
                                                                        <span> = {im.data.fb.interactive.action.parameters.currency} {item.amount.value / item.amount.offset}</span>
                                                                    )}
                                                                </li>
                                                            ))
                                                        }
                                                    </ul>
                                                </div>
                                            )
                                            : null
                                    }
                                    {im.data.fb.interactive.action.parameters.order.subtotal && (
                                        <div className="w3-margin-bottom">
                                            <span className="w3-bold">Total:</span> {im.data.fb.interactive.action.parameters.currency} {im.data.fb.interactive.action.parameters.order.subtotal.value / im.data.fb.interactive.action.parameters.order.subtotal.offset}
                                        </div>
                                    )}
                                    <div className={CHAT_CHIP_CLASS}>Review and Pay</div>
                                </div>
                                : null
                        }
                        {
                            im.data.fb.interactive.action?.name === 'flow'
                                ? <div className={CHAT_CHIP_CLASS}>
                                    {im.data.fb.interactive.action?.parameters.flow_cta}
                                </div>
                                : null
                        }
                        </div>
                        {
                            im?.data?.reactions
                            ?  <div className='w3-flex-inline w3-white w3-border w3-round-xxlarge'>
                                {
                                    Object.entries(im.data.reactions).map(
                                        ([_user_id, reaction]) =>  <div key={_user_id}>{reaction.emoji}</div>
                                    )
                                }
                            </div>
                            : null
                        }
                    </div>
                    <DateViewWithInfo im={im} cur_user_message={cur_user_message} session_data={session_data} />                
                </div>
            </div>
        );
    }
}

function TemplateMessage({im, cur_user_message, session_data}){
    return (
        <div className="w3-center w3-row tw-my-1" id={im.created_at}>                
            <div className="tw-inline-flex tw-flex-col tw-items-center tw-gap-0.5 tw-rounded-lg tw-bg-white/90 tw-px-3 tw-py-1 tw-text-xs tw-text-gray-600 tw-shadow-sm tw-cursor-pointer"
                onClick={() => Popup.show("Payload", <div className="message-view">{objToEl(im.data)}</div>)}
            >
                {im.data1?.ai_follow_up && <div><AiFollowUpBadge /></div>}
                <div>Sent a templated message <span className="tw-font-semibold tw-text-gray-800">{im.data.fb.template.name.toTitleCase()}</span></div>
                <DateViewWithInfo im={im} cur_user_message={cur_user_message} session_data={session_data} />
            </div>
        </div>
    );
}

function AppMessage({im}){
    return (
        <div className="w3-center w3-row tw-m-1" id={im.created_at}>                
            <div className="tw-relative tw-inline-block tw-max-w-[85%] tw-shadow-sm tw-px-4 tw-pt-2 tw-pb-6 tw-bg-gray-50 tw-rounded-lg"
                onClick={() => Popup.show("Payload", (
                    <div className="message-view">
                        {objToEl(im.data, 0, [], (value, indent, path) => {
                            if(path.at(-1) === "scheduled_for" && typeof value === "number"){
                                return <DateView millis={value} />;
                            }
                            return undefined;
                        })}
                    </div>
                ))}
            >
                <div>
                    {/* <p className='tw-text-right tw-text-xs tw-text-gray-600 tw-italic'>System Message</p>  */}
                    <p className='tw-text-sm tw-whitespace-break-spaces tw-my-1'>{im.payload}</p>
                </div>
                <DateView millis={im.created_at} className="tw-absolute tw-bottom-1 tw-right-4 tw-text-xs tw-text-gray-400" />
            </div>
        </div>
    );
}

/* TODO: BATCH THESE CALLS */
function UrlPreview({ url, image_url }) {
    const [url_preview, setUrlPreviews] = useState(null)
    useEffect(
        () => {
            if(!url || !isString(url) || !url.startsWith("http")) return;
            const url_preview = url_previews_cache.get(url);
            if(url_preview) setUrlPreviews(url_preview);
            else {
                axios.get(`/url_preview?url=${url}`).then(
                    (resp) => {
                        setUrlPreviews(resp.data);
                        url_previews_cache.set(url, resp.data);
                    }
                );
            }
        }, []
    );
    if(!url_preview && !image_url) return null;
    return (
        <div onClick={()=>{window.open(url)}}>
            {(url_preview?.image || image_url) && <img className=" w3-border"
                style={{
                    "objectFit": "cover",
                    "marginRight": "4px",
                }}
                src={url_preview?.image || image_url} />
            }
            <p className='w3-padding-2'>{url_preview?.title}</p>
            <p className='w3-padding-2 w3-tiny w3-text-grey'>{url_preview?.description}</p>
        </div>
    )
}

function extractMessage(im) {
    const msg = im?.data?.fb;
    if(msg !== undefined){
        if (msg?.image) {
            return msg.image.caption;
        } else if (msg?.interactive) {
            return msg.interactive.body.text;
        } else if (msg.text) {
            return msg.text.body;
        } else if (msg.template) {
            return msg.template.name;
        }
    }
    else if (im?.payload){
        return im.payload
    }
    else if (im?.data?.txt){
        return im.data.txt;
    }
    return "replied to a message"
}

function InboxMessage({inbox_message: im, user, session_data}){
    const cur_user_message = !im.src_id || im.src_id == user?._id;
    const is_user_in_session = session_data.is_user_in_session;

    if(im._type == INBOX_MESSAGE_TYPE_SIMPLE_MESSAGE){
        return <SimpleMessage 
            user={user} im={im}
            session_data={session_data}
            files={im.payload1 && JSON.parse(im.payload1)}
            tags={im.data?.tags} created_at={im.created_at}
        />;
    }
    else if(im._type == INBOX_MESSAGE_TYPE_WA_TEMPLATE_MSG){
        return <TemplateMessage im={im}
            cur_user_message={cur_user_message}
            session_data={session_data}
        />
    }
    else if (im._type == INBOX_MESSAGE_TYPE_APP) {
        return <AppMessage im={im} />
    }
    else if(im._type == INBOX_MESSAGE_TYPE_WA_INCOMING_MSG){

        const txt = im.data?.txt?.trim();
        const product_items_len = im.data?.order?.product_items?.length;
        const action_txt = !txt 
            ? product_items_len > 0 
                ? `Selected ${product_items_len} item${product_items_len > 1 ? 's' : ''} from WhatsApp catalog.`
                : ''
            : null;
        return <SimpleMessage
            user={user} im={im}
            session_data={session_data}
            txt={txt} files={im.data.media_urls}
            contacts={im.data.contacts}
            location={im.data.location}
            action_txt={action_txt}
            created_at={im.created_at}
            header={
                im.data.referral?.source_url 
                ?   <div className="w3-small w3-padding-sides-4-8 w3-pointer">
                        <UrlPreview url={im.data.referral?.source_url} image_url={im.data.referral?.image_url}/>
                        Refered from:&nbsp;
                        <a href={im.data.referral?.source_url} target="_blank"
                            rel="noopener noreferrer"
                        >
                            {im.data.referral?.source_url}
                        </a>
                    </div>
                : null
            }
        />
    }
    else if(im._type == INBOX_MESSAGE_TYPE_WA_MSG || im._type == INBOX_MESSAGE_TYPE_WA_REPLY_MSG){ /* sent by server */
        let msg_type = im.data?.fb?.type;
        if(msg_type == "text"){
            return <SimpleMessage user={user} im={im}
                session_data={session_data} txt={im.data.fb.text.body}
                created_at={im.created_at}
            />
        }
        else if(msg_type == "contacts"){
            return <SimpleMessage user={user} im={im}
                session_data={session_data} 
                contacts={im.data.fb.contacts}
                created_at={im.created_at}
            />
        }
        else if(msg_type == "location"){
            return <SimpleMessage user={user} im={im}
                session_data={session_data} 
                location={im.data.fb.location}
                created_at={im.created_at}
            />
        }
        else if(["document", "audio", "video", "image"].includes(msg_type)){
            let fb_msg_data = im.data.fb[msg_type];
            if(fb_msg_data){
                return <SimpleMessage user={user} im={im}
                    session_data={session_data} txt={fb_msg_data.caption}
                    files={
                        fb_msg_data.media_urls 
                        || [fb_msg_data.media_url || fb_msg_data.link] // single
                    }
                    created_at={im.created_at}
            />
            }
        }        
        else if(msg_type == "interactive"){
            return <InteractiveMessage user={user} im={im}
                cur_user_message={cur_user_message}
                is_user_in_session={is_user_in_session}
                session_data={session_data}
            />
        }
        else if(msg_type == "template"){
            return <TemplateMessage 
                im={im} 
                cur_user_message={cur_user_message}
                session_data={session_data}
            />
        }
    }
    else if(im._type == INBOX_MESSAGE_TYPE_SESSION_USER_UPDATE){ /*when new user added to session*/
        return (
            <div className="w3-center w3-row tw-m-1">                
                <div className="w3-inline tw-rounded-lg tw-bg-white/90 tw-px-3 tw-py-1 tw-text-xs tw-text-gray-600 tw-shadow-sm">
                    <div><b>{im.user?.name}</b> {im.payload}</div>
                    <DateView millis={im.created_at} className="w3-right tw-text-[10px] tw-text-gray-400" />
                </div>
            </div>
        );
    }
    else if(im._type == INBOX_MESSAGE_TYPE_SESSION_UPDATE){ /*when new user added to session*/
        return (
            <div className="w3-center w3-row tw-m-1">                
                <div className="w3-inline tw-rounded-lg tw-bg-white/90 tw-px-3 tw-py-1 tw-text-xs tw-text-gray-600 tw-shadow-sm">
                    <div>
                        {im.user?.name && <b className='tw-mr-2'>{im.user?.name}</b>}
                        <div onClick={(evt) => Popup.showContextMenu(evt.target, objToEl(im.data))} 
                            className="w3-pointer w3-text-blue"
                        >
                            {im.data?.title || im.payload || "Unknown/Click to View"}
                        </div>
                    </div>
                    <DateView millis={im.created_at} className="w3-right tw-text-[10px] tw-text-gray-400" />
                </div>
            </div>
        );
    }
    else if(im._type == INBOX_MESSAGE_TYPE_DEBUG_LOG){
        return (
            <div className="w3-center w3-row tw-m-1"
                onClick={() => Popup.show("Debug Log", objToEl(im.data))}
            >                
                <div className="w3-inline tw-rounded-lg tw-bg-white/90 tw-px-3 tw-py-1 tw-text-xs tw-text-gray-600 tw-shadow-sm">
                    <div><b>{im.user?.name}</b> {im.payload}</div>
                    <DateView millis={im.created_at} className="w3-right tw-text-[10px] tw-text-gray-400" />
                </div>
            </div>
        );
    }
    else if(im._type == INBOX_MESSAGE_TYPE_PAGES_VIEWED){
        return (
            <div className="w3-center w3-row tw-m-1 tw-break-all">                
                <div className="w3-inline tw-rounded-lg tw-bg-white/90 tw-px-3 tw-py-1 tw-text-xs tw-text-gray-600 tw-shadow-sm">
                    <div><b>{im.user?.name}</b> Page View</div>
                    {
                        im.data?.page_views?.map(
                            (page_view, i) => (
                                <div key={i}>
                                    <div>
                                        <div>{page_view.title || page_view.url}</div>
                                        <DateView millis={page_view.at} className="w3-right tw-text-[10px] tw-text-gray-400" />
                                    </div>
                                </div>
                            )
                        )
                    }
                </div>
            </div>
        );
    }
    else if (ChatSession.renderInboxMessage){
        let ret = ChatSession.renderInboxMessage(im, user, session_data);
        if(ret) return ret;
    }
}

function ChatMessagesAndComposeContainer({session_data, user, options, chat_window_el_ref}){
    const session = session_data.session;
    var inbox_messages = session_data.inbox_messages;
    if(!inbox_messages){
        session_data.inbox_messages = inbox_messages = [];
        session_data.has_more_inbox_messages = null; // load more
    }
    if(!session_data.already_added_messages) session_data.already_added_messages = {};
    const rerenderer = inbox_messages.rerender = useRerender();
    const scrollable_el_ref = useRef(null);
    const [polling_interval, setPollingInterval] = useState(60001); // different

    const adjustPollingInterval = () => {
        const now_millis = new Date().getTime();
        const last_inbox_message_at = last(inbox_messages)?.created_at || now_millis;
        setPollingInterval(
            last_inbox_message_at <  now_millis - 2 * MILLIS_IN_HOUR 
            ? 60000 : 8000
        );
    }

    const addMessages = (im_list) => {
        if(!im_list?.length) return;
        // add refs back into the array
        getByIds({"user_ids": im_list.map((im) => im.src_id)}).then(
            (data) => {
                sortedList(
                    inbox_messages,
                    im_list.filter((im, i) => { // filter duplicates from the inbox messages
                        im.user = data.users[im.src_id];
                        let existing_im = session_data.already_added_messages[im.created_at]
                        if(!existing_im){ // new message
                            session_data.already_added_messages[im.created_at] = im;
                            return true;
                        } else {
                            // update the existing message
                            let pos = binarySearch(
                                inbox_messages,
                                existing_im, 
                                (a, b) => a.created_at - b.created_at
                            );
                            /* only replace if it exsists in inbox_messages */
                            if(inbox_messages[pos]?.created_at == im.created_at) inbox_messages[pos] = im;
                            session_data.already_added_messages[im.created_at] = im;
                            return false;
                        }
                    }),
                    (a, b) => a.created_at - b.created_at
                );
                /* dynamically adjust polling interval */
                adjustPollingInterval();
                rerenderer();
            }
        )
    };

    /* subscribe to events */
    useEffect(
        () => {
            const add_im_message = (im, session_data) => {session_data.session._id == session._id && addMessages([im])};
            broadcaster.add_event_listener("chat:message_sent", add_im_message);
            return () => {
                broadcaster.remove_event_listener("chat:message_sent", add_im_message);
            }
        }, []
    );

    const pollForNewMessages = () => {
        if(inbox_messages.is_loading || session_data.is_dummy_session || session_data.is_inactive === true) return;
        inbox_messages.is_loading = true;

        axios.post(
            "/session/messages",
            {
                "greater_than_timestamp": last(inbox_messages)?.created_at || new Date().getTime(),
                "session_id": session._id
            }
        ).then((resp) => {
            let im_list = resp.data.inbox_messages || [];
            resp.data.refs && im_list.push(...resp.data.refs);
            // so other can listen for new updates
            for(let im of im_list){
                broadcaster.broadcast_event("im:" + session._id, im);
            }
        }).finally(() => inbox_messages.is_loading = false);
    };


    const loadPrevMessages = () => {
        if(inbox_messages.is_loading || session_data.has_more_inbox_messages === false) return;
        inbox_messages.is_loading = true;
        axios.post(
            "/session/messages",
            {
                "less_than_timestamp": inbox_messages?.next_less_than_timestamp,
                "session_id": session._id
            }
        ).then(
            (resp) => {
                if(resp.data.errors) return;
                session_data.has_more_inbox_messages = resp.data.has_more;
                inbox_messages.next_less_than_timestamp = resp.data.next_less_than_timestamp;
                let im_list = resp.data.inbox_messages || [];
                resp.data.refs && im_list.push(...resp.data.refs);
                /* to restore to same scroll position */
                if (scrollable_el_ref.current) {
                    inbox_messages.prev_scroll_height = scrollable_el_ref.current.scrollHeight; // save and restore
                }
                addMessages(im_list);
            }
        ).finally(() => inbox_messages.is_loading = false);
    };


    useOnScroll(
        scrollable_el_ref.current, 
        (percent, scroll_direction) => {
            if(percent == 0){
                loadPrevMessages();
            } else if(percent > 95){
                inbox_messages.stick_scroll_to_bottom = true;
            } else{
                inbox_messages.stick_scroll_to_bottom = false;
            }
        },
        [!inbox_messages?.length]
    );

    // stick to bottom on first load
    useEffect(
        () => {
            if(!inbox_messages) return;
            adjustPollingInterval();
            (inbox_messages.stick_scroll_to_bottom= true);
        },
        [inbox_messages]
    );

    useEffect(
        () => {
            if(!scrollable_el_ref.current || session_data.is_inactive === true) return;
            if(!session_data.is_prev_messages_loaded) loadPrevMessages(); // initial load
            session_data.is_prev_messages_loaded = true;
        }, [session_data.is_inactive, scrollable_el_ref.current]
    );

    useEffect(
        () => {
            /* some init defaults */
            /* listner from allsparkrt */
            const im_listener = (msg) => {
                if(msg._type === INBOX_MESSAGE_TYPE_WA_INFO){  //read/delivered/sent updates
                    let info_data = JSON.parse(msg.payload);
                    let im = session_data.already_added_messages[msg.created_at];
                    im && (im.info= Object.assign(im.info || {}, info_data));
                    rerenderer();
                    return true;
                }
                /* add this message */
                addMessages([msg]);
                return true; // handled
            };
            broadcaster.add_event_listener("im:" + session._id, im_listener);

            /*IMP: set this and cleanup to be used from elsewhere, otherwise it will dangle references and memory bloats*/
            session_data.chat_container_el = scrollable_el_ref.current;
            return () => {
                broadcaster.remove_event_listener("im:" + session._id, im_listener);
                delete session_data.chat_container_el;
            }
        },
        [scrollable_el_ref.current]
    );

    useEffect(
        () => {
            /* poll if user not in session, or explicitly asked for polling */
            var poll_messages_timer = null;
            if(
                !session_data.is_user_in_session 
                || session_data.is_user_in_session.is_dummy
                || options?.polling === true 
                || !isAllsparkConnected()
            ){
                poll_messages_timer = setInterval(() => pollForNewMessages(), polling_interval);
            }
            return () => {
                poll_messages_timer && clearInterval(poll_messages_timer);
            }
        }, [polling_interval]
    );

    useEffect(() => pollForNewMessages(), []); // initial chat open check for new messages

    useEffect(
        () => {
            /* reset scroll to saved state */
            if(scrollable_el_ref && scrollable_el_ref.current){
                if(inbox_messages.stick_scroll_to_bottom){
                    setTimeout(
                        () => {
                            scrollable_el_ref.current?.scrollTo(0, scrollable_el_ref.current.scrollHeight)
                            // clear it otherwise when scrolling it will effect next time new messages loads in the next else loop
                        },
                        0
                    );
                }
                else if(inbox_messages.prev_scroll_height){
                    // restore scroll
                    setTimeout(
                        () => {
                            scrollable_el_ref.current?.scrollTo(
                                0, 
                                scrollable_el_ref.current.scrollHeight - inbox_messages.prev_scroll_height
                            )
                            inbox_messages.prev_scroll_height = null;
                        },
                        0
                    );
                }
            }
        }, [inbox_messages.length, scrollable_el_ref.current]
    );

    
    return (
        <>
            {
                options?.topIndicator
                    ? isFunction(options.topIndicator)
                        ? options.topIndicator(session_data)
                        : options.topIndicator
                    : null
            }
            <div className="w3-flex w3-flex-grow-scroll w3-margin-topbottom-8 w3-relative" ref={scrollable_el_ref}>
                <div className="w3-expand">
                    {
                        inbox_messages.length
                        ?   inbox_messages.map(
                                (im) => {
                                    return <InboxMessage
                                        key={getUKey(im)}
                                        session_data={session_data}
                                        inbox_message={im}
                                        user={user}
                                    />
                                }
                            )
                        :   session_data.has_more_inbox_messages === null
                            ?   <EmptyView title={"Loading.."} height="100%" />
                            :   <EmptyView title={"No Messages"} height="100%" />
                    }
                </div>
            </div>
            {
                options?.bottomStatusIndicator  // legacy
                ?   isFunction(options.bottomStatusIndicator)
                    ?   options.bottomStatusIndicator(session_data)
                    :   options.bottomStatusIndicator
                :   ChatSession.renderBottomStatusIndicator 
                    && ChatSession.renderBottomStatusIndicator(session_data)
            }
            <div className="w3-padding-4-8">
                {
                    user && isWriteAllowed(session_data)
                    ?   <ComposeMessage session_data={session_data}
                            user={user}
                            options={options}
                            chat_window_el_ref={chat_window_el_ref}
                        />
                    :   <div className="w3-center">
                            {
                                !user
                                ?   <span>
                                        You need <b onClick={(evt) => broadcaster.broadcast_event("do_login:chat", session_data, options)}>
                                            Login
                                        </b> to chat.
                                    </span>
                                :   <span>You cannot send messages here.</span>
                            }
                        </div>  
                }
            </div>
        </>
    );
}


const isReadAllowed = (session_data) => {
    const session = session_data.session;
    const is_user_in_session = session_data.is_user_in_session;
    return session.is_public
        || session.is_read_allowed
        || (is_user_in_session && is_user_in_session.session_user_type <= SESSION_USER_GENERAL)
};

const isWriteAllowed = (session_data) => {
    const session = session_data.session;
    const is_user_in_session = session_data.is_user_in_session;
    return session.is_public
        || (is_user_in_session && is_user_in_session.session_user_type <= session.write_permission)
}

function ComposeMessage({session_data, user, chat_window_el_ref}){
    const [to_send_text, setToSendText] = useState("");
    const [to_send_files, setToSendFiles] = useState([]);
    const text_el_ref = useRef();
    const MAX_ATTACHMENTS = 3;
    const [uploading, setUploading] = useState(null);

    useEffect(
        () => {
            // Optional 2nd arg: files array (same shape as attachments). When only
            // text is broadcast, the 2nd arg is the broadcaster count (a number).
            const onToSendText = (text, files) => {
                setToSendText(text ?? "");
                if (Array.isArray(files)) {
                    setToSendFiles(files);
                }
            };
            broadcaster.add_event_listener("chat:to_send_text", onToSendText);
            return () => {
                broadcaster.remove_event_listener("chat:to_send_text", onToSendText);
            }
        }, []
    ) ;

    const attachFilesPopup = () => {
        if (uploading) return;
        var popup = Popup.show(
            "Add Files",
            <FileUploader
                allowed_mime_types={[
                    "image/jpeg", 'image/png', 'application/pdf',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'video/mp4'
                ]} 
                files={to_send_files}
                max_files={MAX_ATTACHMENTS}
                onFilesUpdated={(files) => {setToSendFiles(files); popup.close()}}
            />,
            {"container": chat_window_el_ref.current}
        );
    }

    const showEmojisPopup = (evt) => {
        Popup.showContextMenu(
            evt.target,
            <div className='w3-flex-row-wrap w3-scroll-y w3-large w3-padding-8 app-smileys w3-center'
                style={{maxHeight: "200px", width: "200px"}}
                onClick={
                    (evt) => {
                        let text_el = text_el_ref.current;
                        let [start, end] = text_el.last_cursor || [0, 0];
                        text_el.focus();
                        setToSendText(to_send_text.slice(0, start) + evt.target.textContent + to_send_text.slice(end));
                        text_el.selectionStart = text_el.selectionEnd = start + 1;
                    }
                }
            >
                {
                    EMOJIS.map(
                        (smiley) => <span key={smiley}>{smiley}</span>
                    )
                }
            </div>
        )
    }

    const sendMessage = () => {
        if((!to_send_text && to_send_files.length === 0) || uploading) return;
        let data = {
            "dest_session_id": session_data.session._id,
            "payload": to_send_text,
            "_type": INBOX_MESSAGE_TYPE_SIMPLE_MESSAGE,
        };
        if(to_send_files.length){
            data["payload1"] = JSON.stringify(to_send_files);
        }
        if(session_data.is_dummy_session){
            data["is_dummy_session"] = true;
        }

        const im = Object.assign({}, data);
        /* local data on message */
        im.created_at = new Date().getTime();
        const local_message_id = im.local_id = "local__" + im.created_at;
        im.user = user;
        session_data.inbox_messages.stick_scroll_to_bottom = true;
        session_data.inbox_messages.push(im);
        session_data.inbox_messages.rerender();

        setToSendText(""); 
        setToSendFiles([]);

        axios.post("/session/send", im).then(
            (resp) => {
                resp = resp.data;
                let pos = 0;
                for(let i=session_data.inbox_messages.length - 1; i >= 0; i--){
                    if(session_data.inbox_messages[i].local_id == local_message_id){
                        pos = i;
                        break;
                    }
                }
                if(resp.type == TYPE_INBOX_ENTRY_ADDED){
                    if(resp.new_session_created){
                        /*update the new session created*/
                        Object.assign(session_data, resp.new_session_created);
                        session_data.is_dummy_session = false;
                        session_data.has_more_inbox_messages = false;
                    }
                    if(resp.is_user_in_session){
                        /* new user joined ? */
                        session_data.is_user_in_session = resp.is_user_in_session;
                    }
                    const im = resp.inbox_message;
                    im.user = user;
                    session_data.inbox_messages.splice(pos, 1); // remove local message
                    broadcaster.broadcast_event('chat:message_sent', im, session_data);
                }
                else if(resp.type == TYPE_ERROR){
                    session_data.inbox_messages.splice(pos, 1);
                }
            }
        );
    }

    const onChatPaste = async (evt) => {
        const selected_files = evt.clipboardData.files;
        if (!selected_files?.length) return;
        if (selected_files.length > 1) {
            Popup.toast(
                <div className='tw-rounded tw-p-2 tw-text-sm'>Please copy & paste one file at a time.</div>,
                2500
            )
            evt.preventDefault();
            return;
        }
        if (to_send_files.length === MAX_ATTACHMENTS) {
            Popup.toast(
                <div className='tw-rounded tw-p-2 tw-text-sm'>You can only send upto 3 attachments</div>,
                2500
            )
            evt.preventDefault();
            return;
        }
        let file = selected_files[0];
        validateAndUploadFiles({
            file_data_objs: [file],
            allowed_mime_types: ["image/jpeg", 'image/png', 'application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'video/mp4'],
            setStatus: setUploading,
            onFilesUploaded: (new_files) => {
                setToSendFiles([...new_files, ...to_send_files])
            }
        })
    }

    useEffect(
        () => {
            const debouced_timer = setTimeout(
                () => {
                    const event_wait_until = text_el_ref.current?.blocked_until || 0;
                    const cur_millis = new Date().getTime();
                    if(event_wait_until > cur_millis  || (!to_send_text && to_send_files.length === 0)){
                        return;
                    }
                    broadcaster.broadcast_event("chat:typing", session_data, text_el_ref.current);
                    text_el_ref.current.blocked_until = cur_millis + 10000; // don't raise another event until next 10 seconds
                }, 
                1000
            );
            return () => clearTimeout(debouced_timer);
        }, 
        [to_send_text, to_send_files]
    );
    
    return (
        <div className="w3-list-horizontal w3-flex-row w3-flex-vcenter">
            <div className='w3-xlarge w3-list w3-flex-col'>
                <Smile onClick={showEmojisPopup} size={26} />
                <div className="w3-relative">    
                    <Plus className={to_send_files.length ? `w3-text-red` : ''} 
                        onClick={attachFilesPopup}
                        size={26}
                    />
                    {to_send_files.length ? <div className='w3-tiny w3-absolute w3-text-white w3-red w3-round-medium w3-center' style={{top:-8, right: -5, width:16, height: 16}}>{to_send_files.length}</div> : null}                   
                </div>
            </div>
            <textarea
                className="w3-round w3-input w3-border-black w3-flex-grow-s1"
                value={to_send_text}
                onChange={(evt) => setToSendText(evt.target.value)}
                onTouchMoveCapture={(evt) => evt.stopPropagation()}
                onBlur={
                    (evt) => {
                        evt.target.last_cursor = [evt.target.selectionStart, evt.target.selectionEnd];
                    }
                }
                onPaste={onChatPaste}
                disabled={uploading}
                ref={text_el_ref}
            />
            {uploading ? <span className='w3-absolute w3-small tw-flex tw-gap-x-0.5 tw-items-center' style={{left: '35%'}}><LoaderCircle className="tw-animate-custom-spin" size={18}  />&nbsp; Uploading</span> : null}
            <CircleArrowRight className="" size={45} onClick={sendMessage} />
        </div>
    );
}

function EditChatSession({session_data, afterUpdate, className}){
    const session = session_data.session;
    const ctx = useRef({"updates": {}}).current; // context
    const doUpdateChatSession = () => {
        if(ctx.is_loading) return;
        ctx.is_loading = true;
        axios.post("/session/update", {"session_id": session._id, ...ctx.updates}).then(
            (resp) => {
                if(resp.data.errors){
                    Popup.error(resp.data.errors);
                    return;
                }
                Object.assign(session, resp.data.session);
                afterUpdate && afterUpdate(session_data);
                ctx.updates = {};
            }
        ).finally(() => ctx.is_loading = false);
    }

    return <div className={className}>
        <div className='w3-margin-bottom'>
            <div>Title</div>
            <input type="text" className="w3-input w3-border w3-round" defaultValue={session.title} 
                onChange={(evt) => ctx.updates.title = evt.target.value}
            />
        </div>
        <div className='w3-margin-bottom'>
            <div>Description</div>
            <textarea className="w3-input w3-border w3-round" defaultValue={session.description} 
                onChange={(evt) => ctx.updates.description = evt.target.value}
            />
        </div>
        <div className='w3-margin-bottom'>
            <div>Group Image</div>
            <FileUploader files={session.image ? [session.image] : []} 
                onFilesUpdated={(files) => ctx.updates.image = files[0]?.url} 
            />
        </div>
        <div className='w3-margin-bottom'>
            <label className='w3-flex w3-list-horizontal-16'>
                <input type="checkbox" defaultChecked={!session.is_public} 
                    onChange={(evt) => {
                        if(!evt.target.checked){
                            if(!window.confirm("Are you sure you want to make this group public?")){
                                evt.target.checked = true;
                                return;
                            }
                        }
                        ctx.updates.is_public = !evt.target.checked;
                    }}
                    className='w3-check'
                />
                <div>Private Group</div>
            </label>  
        </div>
        <div className='w3-margin-bottom'>
            <label className='w3-flex w3-list-horizontal-16'>
                <input type="checkbox" defaultChecked={session.is_public || session.is_read_allowed} 
                    className='w3-check'
                    onChange={(evt) => {
                        if(evt.target.checked){
                            if(!window.confirm("Are you sure you want to allow non-members to read messages/posts in this group?")){
                                evt.target.checked = false;
                                return;
                            }
                        }
                        ctx.updates.is_read_allowed = evt.target.checked;
                    }}
                />
                <div>Allow Non-Members to read</div>
            </label>
        </div>
        <div>
            <div className='w3-button w3-green' onClick={doUpdateChatSession}>Update</div>
        </div>
    </div>
}

function ChatSessionSettings({session_data, options}){
    const is_user_in_session = session_data.is_user_in_session;
    const is_user_admin = is_user_in_session && is_user_in_session.session_user_type <= SESSION_USER_ADMIN;
    const doToggleNotficationMute = () => {
        EventTracker.track(PAGE_EVENTS.CLICKED_ON_MUTE_NOTIFICATION);
        axios.post(
            "/session/user/update",
            {
                "session_id": session_data.session._id,
                "notification_flag": (
                    is_user_in_session.notification_flag > NOTIFICATION_FLAG_ONLY_WHEN_TAGGED 
                        ? NOTIFICATION_FLAG_ONLY_WHEN_TAGGED : NOTIFICATION_FLAG_ALL
                )
            }
        ).then(
            (resp) => {
                resp.data.is_user_in_session && Object.assign(session_data.is_user_in_session, resp.data.is_user_in_session);
                session_data.rerender?.();
            }
        )
    }

    return (
        <div>
            {
                (ChatSessionSettings.customSettings && ChatSessionSettings.customSettings(session_data, options))
                || (
                    <div className='w3-list w3-list-bordered w3-bold w3-margin-top w3-border-bottom'>
                        {
                            is_user_in_session && !is_user_in_session.is_dummy
                            ?   <div className="w3-row w3-padding-button" onClick={doToggleNotficationMute}>
                                    {
                                        is_user_in_session.notification_flag > NOTIFICATION_FLAG_ONLY_WHEN_TAGGED
                                        ?   <p className='tw-flex tw-flex-row tw-gap-x-1 tw-items-center'><Bell className="w3-text-red" size={18} /> Mute Notifications</p>
                                        :   <p className='tw-flex tw-flex-row tw-gap-x-1 tw-items-center'><BellOff className="w3-text-green" size={18} /> Unmute Notifications</p>
                                    }                                        
                                </div>
                            :   null
                        }
                        {
                            is_user_admin
                            ?   <div className="w3-padding-button tw-flex tw-flex-row tw-gap-x-1 tw-items-center"
                                    onClick={(evt) => {
                                        const popup = Popup.show(
                                            "", <EditChatSession session_data={session_data} afterUpdate={() => {popup.close()}} className="w3-padding"/>
                                        )
                                    }}
                                >
                                    <SquarePen className="w3-text-blue" size={18} /> Edit
                                </div>
                            :  null
                        }
                    </div>            
                )
            }
            <div className='w3-padding-8'>
                <ChatSessionUsers session_data={session_data} />
            </div>
        </div>
    )

}

/* one chat window with multiple sessions */
function ChatSession({
    session_ids, session_data_list:_session_data_list,
    onClose, options, window_id
}){
    /* current session */
    const [session_data_list, setSessionDataList] = useState(_session_data_list);
    const [session_data, setActiveSessionData] = useState(_session_data_list?.[0]);
    const [show_tabs, setShowTabs] = useState(null);
    const user = useCurrentUser();
    const {is_minimized, setIsMinimized, width, startResize} = useChatWindowMinimized({
        window_id, session_data, session_data_list, user_id: user?._id, onClose
    });
    const chat_window_el_ref = useRef(null);
    const swipe_el_ref = useRef(null);
    const rerender = useRerender();
    const [ViewPort] = useBroadcastedState("viewport");
    session_data && (session_data.rerender= rerender); // so we can global rerender  

    /* swipe sizes slides only on window resize, re-measure when the chat window is resized */
    useEffect(() => {swipe_el_ref.current?.swipe?.setup()}, [width]);

    /* load initial sessions */
    useEffect(
        () => {
            if(!session_data_list || !session_data_list.length) return;
            setShowTabs(session_data_list.some((session_data) => session_data.tab_title));    
        }, [session_data_list]
    )
    useEffect(
        () => {
            if(!session_ids?.length) return;
            getByIds({"session_ids": session_ids}).then(
                function(data){
                    let session_data_list = session_ids.map((session_id) => data.sessions[session_id])
                        .filter((session) => session);
                    if(!session_data_list.length){
                        GenericException.showPopup("This session is no longer available.");
                        return;
                    };
                    setSessionDataList(session_data_list);
                    setActiveSessionData(session_data_list[0]);
                }
            );
        }, [session_ids]
    );

    const swipeOptions = useMemo( // necessary to prevent re-render of swipe component that resets it 
        () => {
            return {
                "continuous": false, // true doesn't work, it's sliding 2 slide at a time, annoying issue
                "callback": (index, elem) => {
                    let active_session = session_data_list[index]; //swipe_el_ref.current.getPos()
                    active_session && setActiveSessionData(active_session);
                }
            }
        }, [session_data_list]
    );

    /* rendered by MinimizedChatsBar in chat_windows.jsx */
    if(is_minimized) return null;
    if(!session_data) {
        return (
            <div className="w3-relative w3-white w3-flex-col">
                <LoadingView title="Loading..." />
            </div>
        )
    }
    const session = session_data.session;
    const is_user_in_session = session_data.is_user_in_session;
    const IS_LAGRGE_SCREEN = ViewPort.WIDTH > 768;
    return (
        <div className={`w3-relative w3-border w3-bounded w3-shadow w3-white ${ IS_LAGRGE_SCREEN ? "w3-round-xlarge w3-padding-2" : ""}`}
            style={{height: Math.min(800, ViewPort.HEIGHT) + "px"}}
        >
            {IS_LAGRGE_SCREEN ? <div className='chat-window-resize-handle' onPointerDown={startResize} title='Drag to resize' /> : null}
            <div className={`w3-relative w3-expand w3-bounded w3-overflow-hidden w3-right w3-white w3-flex-col ${IS_LAGRGE_SCREEN ? "w3-round-xlarge" : ""}`}
                style={{
                    "width": (IS_LAGRGE_SCREEN ? width : 450) + "px",
                    "backgroundImage": `url(${options?.background_image || ChatSession.BACKGROUND_IMAGE || CHAT_DEFAULT_BG_IMAGE})`,
                    "backgroundRepeat": 'repeat',
                    "transform": "translateZ(0)",
                }}
                ref={chat_window_el_ref}
            >
                {/* header */}
                <div className={`w3-shadow w3-no-wrap w3-relative w3-flex w3-flex-vcenter ${ChatSession.theme?.header_classname || "w3-teal"}`}>
                    {
                        session?.image
                        ?   <img className="w3-circle w3-border" 
                                style={{
                                    "height" :"35px",
                                    "width": "35px",
                                    "objectFit": "cover",
                                    "marginRight": "4px",
                                }}
                                src={session.image.url || session.image}
                            />
                        :   null
                    }
                    {/* title */}
                    <div className="w3-bold w3-overflow-hidden w3-padding-left"
                        onClick={
                            options?.onTitleClick 
                            ? options.onTitleClick 
                            : () => Popup.show("", <div className='w3-padding'>{session.title}</div>)
                        }
                    >
                        {
                            (ChatSession.renderChatTitle && ChatSession.renderChatTitle(session_data))
                            || session.title
                        }
                    </div>
                    {/* settings */}
                    <div className="w3-margin-left-auto">
                        {
                            is_user_in_session && is_user_in_session.notification_flag <= NOTIFICATION_FLAG_ONLY_WHEN_TAGGED
                            ?   <BellOff size={22} className="w3-button !tw-p-0.5 tw-mx-1 tw-my-1.5" onClick={() => {}}
                                />
                            :   null
                        }
                        <Settings size={22} className="w3-button !tw-p-0.5 tw-mx-1 tw-my-1.5" onClick={() => {
                            EventTracker.track(PAGE_EVENTS.CLICKED_ON_SETTINGS);
                            ChatSession.Settings 
                                ?   Popup.open(
                                        "", 
                                        <ChatSession.Settings session_data={session_data} options={options} />,
                                        {"container": chat_window_el_ref.current, "className": "w3-absolute"}
                                    )
                                :   Popup.open(
                                        "",
                                        <ChatSessionSettings session_data={session_data} options={options} />,
                                        {"container": chat_window_el_ref.current, "className": "w3-absolute"}
                                    );
                        }}/>
                        <Minimize2
                            className="w3-button !tw-p-0.5 tw-mx-1 tw-my-1.5"
                            onClick={() => setIsMinimized(true)}
                            size={22}
                        />
                        <XMarkIcon
                            className="w3-button !tw-p-0.5 tw-mx-1 tw-my-1.5"
                            onClick={
                                () => onClose && onClose()
                            }
                            size={22}
                        />
                    </div>
                </div>
                {
                    show_tabs
                    ?   <div className="w3-row w3-2px-shadow w3-white w3-flex w3-flex-row w3-scroll-x w3-hide-scrollbar">
                            {session_data_list.map((_session_data, i) => {
                                _session_data.is_inactive = _session_data.session._id != session_data.session._id;
                                return (
                                    <div className="w3-padding-button w3-bold w3-center w3-no-wrap"
                                        key={_session_data.session._id}
                                        style={{
                                            "borderRight": i < session_data_list.length - 1 ? "1px solid #ccc" : "none",
                                            "borderBottom": !_session_data.is_inactive ? "2px solid red" : "1px solid #ccc"
                                        }}
                                        onClick={() => {
                                            swipe_el_ref.current?.slide(i, 500);
                                            setActiveSessionData(_session_data)
                                        }}
                                    >
                                        {
                                            (ChatSession?.renderChatTab && ChatSession.renderChatTab(_session_data))
                                            ||  _session_data.tab_title || "Tab " + (i + 1)
                                        }
                                    </div>
                                )
                            })}
                        </div>
                    :   null
                }
                <ReactSwipe swipeOptions={swipeOptions}
                    className='w3-flex w3-flex-grow-s1 w3-overflow-hidden'
                    ref={swipe_el_ref}
                    childCount={(session_data_list?.length || 0) + (options?.swipeViews?.length || 0)} // force dependency
                    style={{wrapper: {display: "flex"}}}
                >
                    {
                        session_data_list.map((_session_data) => {
                            _session_data.is_inactive = _session_data.session._id != session_data.session._id;
                            return <div className='w3-flex-col w3-relative' key={_session_data.session._id}>
                                <ChatMessagesAndComposeContainer
                                    session_data={_session_data}
                                    user={user}
                                    options={options}
                                    chat_window_el_ref={chat_window_el_ref}
                                />
                            </div>
                        })
                    }
                    {options?.swipeViews}
                </ReactSwipe>
            </div>
        </div>
    );
}

/* while rendering list of users in chat session */
function ChatSessionUser({session_data, csu}){
    const is_user_in_session = session_data.is_user_in_session;
    const is_user_admin = is_user_in_session?.session_user_type <= SESSION_USER_ADMIN;
    const is_user_moderator = is_user_in_session?.session_user_type <= SESSION_USER_MODERATOR;
    const session = session_data.session;
    const refresh = useRerender();

    const doAction = (action) => {
        axios.post(
            "/session/user/update",
            {"session_id": session._id, "for_user_id": csu.user_id, "action": action}
        ).then(
            (resp) => {
                resp.data.session_user && Object.assign(csu, resp.data.session_user);
                refresh();  
            }
        )
    }

    const showOptions = (evt) => {
        Popup.showContextMenu(
            evt.target, 
            <div className="w3-list w3-white">
                {
                    csu.session_user_type == SESSION_USER_PENDING
                    &&  <>
                            <div className="w3-padding-8 w3-text-green" onClick={() => doAction("approve")}>Approve</div>
                            <div className="w3-padding-8 w3-text-red" onClick={() => doAction("reject")}>Reject</div>
                        </>                                
                }
                {
                    csu.session_user_type == SESSION_USER_BLOCKED
                    &&  <div className="w3-padding-8 w3-text-red" onClick={() => doAction("unblock")}>Unblock</div>
                }
                {
                    csu.session_user_type <= SESSION_USER_GENERAL
                    &&  <div className="w3-padding-8 w3-text-red" onClick={() => doAction("block")}>Block user</div>
                }
                {
                    is_user_admin && csu.session_user_type == SESSION_USER_GENERAL
                    ?  <div className="w3-padding-8 w3-text-red" onClick={() => doAction("make_moderator")}>Make Moderator</div>
                    :  null
                }
            </div>
        );
    }
    return (
        <div className='w3-flex-row w3-list-horizontal-16 w3-padding-top'>
            <div className="w3-col s2 w3-padding-sides-8">
                <img src={csu.user.image?.url || csu.user.image || ProfileAvatar} alt={csu.user.name} width="30px" height="auto" />
            </div>            
            <div className="w3-flex-grow-s1">
                <div className="w3-bold">{csu.user.name}</div>
                <div className='w3-flex'>
                    {
                        csu.session_user_type == SESSION_USER_ADMIN 
                        ? <div className='w3-green w3-tag'>Admin</div>
                        : csu.session_user_type == SESSION_USER_MODERATOR
                            ? <div className='w3-blue w3-tag'>Moderator</div>
                            :  csu.session_user_type == SESSION_USER_BLOCKED
                                ?   <div className='w3-red w3-tag'>Blocked by you</div>
                                :   csu.session_user_type == SESSION_USER_PENDING
                                    ? <div className='w3-orange w3-tag'>Awaiting Approval</div>
                                    :  csu.session_user_type >= SESSION_USER_BANNED
                                        ? <div className='w3-red w3-tag'>Blocked</div>
                                        : null
                    }
                    {
                        is_user_moderator && csu.data?.app
                        ? <div className='w3-yellow'>{csu.data.app}</div>
                        :  null
                    }
                    {ChatSessionUser.customDescriptionRenderer?.(csu)}
                </div>
            </div>
            {
                is_user_moderator
                ?   <EllipsisVertical className="" size={18} onClick={showOptions}/>
                :   null
            }
        </div>
    )
}

function ChatSessionUsers({session_data}){
    const session = session_data.session;
    const [csu_list, setCSUList] = useState([]);
    const [session_users_count, setSessionUsersCount] = useState(0);
    const ctx = useRef({}).current;
    const session_users_list_el = useRef(null);

    const loadMore = () => {
        if(ctx.is_loading || ctx.has_more === false) return;
        ctx.is_loading = true;
        axios.post(
            "/session/users",
            {"session_id": session._id, "cursor": ctx.cursor || 0}
        ).then(
            async (resp) => {
                if(resp.data.errors) return;
                ctx.has_more = resp.data.has_more;
                ctx.cursor  = resp.data.cursor;
                resp.data.session_users_count && setSessionUsersCount(resp.data.session_users_count);
                if(!resp.data.session_users?.length) return;
                /* fetch users */
                let cache = await getByIds({"user_ids": resp.data.session_users.map((csu) => csu.user_id)})
                resp.data.session_users.forEach((csu) => {
                    csu.user = cache.users[csu.user_id];
                });
                setCSUList([...csu_list, ...resp.data.session_users]);
            }
        ).finally(() => {ctx.is_loading = false});
    };

    useEffect(() => {loadMore()}, [session._id]); // init
    useOnScroll(session_users_list_el.current, (percent) => {percent > 95 && loadMore();}, []); // scroll 

    return <div className='w3-row'>
        {
            session_users_count
            ?   <div><b>{session_users_count}</b> Members</div>
            :   null
        }
        <div className='w3-list'>
            {csu_list.map((csu) => <ChatSessionUser session_data={session_data} csu={csu} key={csu.user_id}/>)}
        </div>
    </div>
}

/* themed container for chat windows, see chat_windows.jsx */
const getChatWindowsContainer = () => chatWindowsContainer(() => ChatSession.theme?.header_classname);

function SessionListItem({session_data}){
    const rerender = useRerender();
    const is_user_in_session = session_data.is_user_in_session;
    const session = session_data.session;
    return (
        <div className={`w3-flex w3-relative w3-ripple1 ${is_user_in_session?.unseen_count ? "w3-light-grey": ""}`}
            key={session._id}
            onClick={() => {
                ChatSession.open(session_data);
                is_user_in_session.unseen_count= 0;
                rerender();
            }}
        >
            {
                is_user_in_session?.unseen_count 
                ?   <div className="w3-display-topright w3-padding-8">
                        <div className="w3-tiny w3-center w3-inline w3-red w3-circle w3-middle-align"
                            style={{height: "20px", width:"20px"}}
                        >
                            {is_user_in_session.unseen_count}
                        </div>
                    </div>
                :   null
            }
            <div className="w3-flex-grow w3-padding-8-4">
                <div className="title w3-bold">
                    {session.title}
                </div>
                <div className="w3-padding-topbottom-8 w3-small w3-text-light-grey">
                    {session.description}
                </div>
                <div>
                {
                    session_data.tab_title
                    ?   <div className="w3-tag" component="auto_bg_color">
                            {session_data.tab_title}
                        </div>
                    :   null
                }
                </div>
                <DateView className="w3-tiny w3-text-grey" millis={is_user_in_session?.updated_at || session.created_at} />
            </div>
            {
                session.image
                ?   <div className="w3-col w3-center s3 w3-padding-4">
                        <img src={session.image} alt="image" className='w3-circle' width="50px" height="auto" />
                    </div>
                :   null
            }
        </div>
    )
}

function LoadUserChats({onLoad, className}){
    const [session_data_list, setSessionDataList] = useState(null);
    const ctx = useRef({}).current;
    const load_more_chats = () => {
        if(ctx.is_loading || ctx.has_more === false) return;
        ctx.is_loading = true;
        axios.post("/user/chats", {"cursor": ctx.cursor}).then((resp) => {
            setSessionDataList(resp.data.sessions);
            ctx.cursor = resp.data.cursor;
            ctx.has_more = resp.data.has_more;
        }).finally(() => {ctx.is_loading = false});
    }

    useEffect(() => {load_more_chats()}, []); // init
    useOnScroll(ctx.scrolling_list_el, (percent) => {percent > 95 && load_more_chats();}, [ctx.scrolling_list_el]);

    return (
        <div className={`w3-pointer w3-bounded w3-scroll ${className || ''}`}>
            <div className='w3-row w3-scroll-y w3-list-bordered w3-list' ref={(el) => {ctx.scrolling_list_el= el;}}>
                {
                    session_data_list?.map(
                        (session_data) => <SessionListItem session_data={session_data} key={session_data.session._id}/>
                    ) || <LoadingView title={"Loading.."}  height="200px"/>
                }
            </div>
        </div>
    );
}

/* takes session_ids or array of session_data and render the tabs */
ChatSession.open = (sessions, options) => {
    if(!Array.isArray(sessions)){
        sessions = [sessions];
    }
    const session_ids = [];
    const session_data_list = sessions.filter(
        (session) => {return isString(session) ? (session_ids.push(session) && false) : true}
    );
    const _window_id = [
        ...(session_ids || []), 
        ...(session_data_list?.map(sd => sd.session._id) || [])
    ].sort().join("_");

    let container = getChatWindowsContainer();
    if (_window_id in container.views) {
        maximizeChatWindow(_window_id);
        return
    }

    const ctx = {};
    /* reset overScrollBehaviour ans overscrolling is annoying n chat window */
    document.body.style.overscrollBehavior = "none";
    document.documentElement.style.overscrollBehavior = "none";

    ctx.close = () => {
        container.remove(_window_id);
        if(!chatWindowKeys(container).length){
            /* all chat windows closed */
            document.body.style.overscrollBehavior = null;
            document.documentElement.style.overscrollBehavior = null;    
        }
    }

    container.add(
        _window_id,
        <ChatSession
            session_ids={session_ids}
            session_data_list={session_data_list}
            key={_window_id}
            window_id={_window_id}
            options={options}
            onClose={ctx.close}
        />
    );
    
    return ctx;
}

ChatSession.closeAll = () => {
    let container = getChatWindowsContainer();
    chatWindowKeys(container).forEach((key) => container.remove(key));
}

function SingleChatSessionPage() {
    var {session_id} = useParams();
    useEffect(() => {ChatSession.open(session_id)}, []);
    return <div className='w3-content w3-center'>
        <img 
            src="https://storage.googleapis.com/sukhiba/mobilefresh/user_uploads/2023-02-28/ztc1iALAep1677594459__sukhiba.jpeg" className='w3-image'
        />
    </div>
}


export {
    ChatSession, LoadUserChats, SingleChatSessionPage,
    SESSION_USER_ADMIN, SESSION_USER_GENERAL, SESSION_USER_MODERATOR,
    INBOX_MESSAGE_TYPE_WA_MSG, INBOX_MESSAGE_TYPE_WA_TEMPLATE_MSG,
    INBOX_MESSAGE_TYPE_WA_INCOMING_MSG, INBOX_MESSAGE_TYPE_SESSION_UPDATE,
    INBOX_MESSAGE_TYPE_SESSION_USER_UPDATE, INBOX_MESSAGE_TYPE_WA_REPLY_MSG,
    isReadAllowed, isWriteAllowed, ChatSessionUsers, EditChatSession,
    InboxMessage, SimpleMessageContextMenu
};
